import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, AlertCircle, Ban } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  'Alcohol',
  'Tobacco',
  'Gambling',
  'Weapons & Defense',
  'Adult Entertainment',
  'Fossil Fuels',
  'Animal Testing',
  'Religious Restrictions',
  'Environmental Concerns',
  'Other'
];

const ITEM_TYPES = [
  { value: 'asset_class', label: 'Asset Class' },
  { value: 'sector', label: 'Sector' },
  { value: 'theme', label: 'Theme/Industry' },
  { value: 'security', label: 'Specific Security' }
];

const NoGoList = ({ clientId, clientName }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  
  // New item form
  const [newItem, setNewItem] = useState({
    type: 'theme',
    name: '',
    category: '',
    reason: ''
  });

  useEffect(() => {
    fetchNoGoList();
  }, [clientId]);

  const fetchNoGoList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/no-go-list/${clientId}`, config);
      
      if (response.data) {
        setItems(response.data.items || []);
        setNotes(response.data.notes || '');
      }
    } catch (error) {
      console.error('Error fetching No Go List:', error);
      toast.error('Failed to fetch No Go List');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    if (!newItem.name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    if (!newItem.category) {
      toast.error('Please select a category');
      return;
    }
    
    setItems([...items, { ...newItem, added_at: new Date().toISOString() }]);
    setNewItem({ type: 'theme', name: '', category: '', reason: '' });
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const saveNoGoList = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(
        `${API}/no-go-list?client_id=${clientId}`,
        { items, notes },
        config
      );
      
      toast.success('No Go List saved successfully');
    } catch (error) {
      console.error('Error saving No Go List:', error);
      toast.error('Failed to save No Go List');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Ban className="w-6 h-6 text-red-600" />
          No Go List - {clientName}
        </h3>
        <p className="text-gray-600 mt-1">
          Securities and asset classes the client does not want to invest in
        </p>
      </div>

      {/* Add New Item */}
      <Card className="border-ugmonk-border bg-[#fffdf0]">
        <CardHeader className="pb-3">
          <CardTitle className="text-ugmonk-dark flex items-center gap-2 text-lg">
            <Plus className="w-5 h-5 text-ugmonk-dark" />
            Add Restriction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">Type</Label>
              <select
                value={newItem.type}
                onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
              >
                {ITEM_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <Label className="text-gray-700">Category *</Label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
              >
                <option value="">Select category...</option>
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <Label className="text-gray-700">
              {newItem.type === 'asset_class' ? 'Asset Class Name' :
               newItem.type === 'sector' ? 'Sector Name' :
               newItem.type === 'theme' ? 'Theme/Industry Name' :
               'Security Name'} *
            </Label>
            <Input
              placeholder={
                newItem.type === 'asset_class' ? 'e.g., Equities, Fixed Income' :
                newItem.type === 'sector' ? 'e.g., Alcohol & Beverages' :
                newItem.type === 'theme' ? 'e.g., Tobacco, Gambling' :
                'e.g., ITC Limited'
              }
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              className="bg-white border-gray-300"
            />
          </div>

          <div>
            <Label className="text-gray-700">Reason (Optional)</Label>
            <Input
              placeholder="e.g., Religious beliefs, Environmental concerns"
              value={newItem.reason}
              onChange={(e) => setNewItem({ ...newItem, reason: e.target.value })}
              className="bg-white border-gray-300"
            />
          </div>

          <Button
            onClick={addItem}
            className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add to No Go List
          </Button>
        </CardContent>
      </Card>

      {/* Current No Go List */}
      <Card className="border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            Current Restrictions ({items.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Ban className="w-16 h-16 mx-auto mb-3 text-gray-300" />
              <p>No restrictions added yet</p>
              <p className="text-sm mt-1">Add items above to build the No Go List</p>
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="p-4 border-l-4 border-red-500 bg-red-50 rounded-lg"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                          {ITEM_TYPES.find(t => t.value === item.type)?.label || item.type}
                        </span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                          {item.category}
                        </span>
                      </div>
                      <h4 className="font-semibold text-gray-900">{item.name}</h4>
                      {item.reason && (
                        <p className="text-sm text-gray-600 mt-1">Reason: {item.reason}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Added: {new Date(item.added_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeItem(index)}
                      className="border-red-500 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional Notes */}
      <Card className="border-gray-200 bg-white">
        <CardHeader>
          <CardTitle className="text-gray-900">Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional restrictions or preferences..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={saveNoGoList}
          disabled={saving}
          className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
        >
          {saving ? 'Saving...' : 'Save No Go List'}
        </Button>
      </div>
    </div>
  );
};

export default NoGoList;
